import { useEffect, useState } from 'react';
import { Api } from '@infra/api';
import { Analyses } from '@domain/models';
import { Modal } from '../components/Modal';
import { SearchSelect } from '../components/SearchSelect';
import { DataTable } from '../components/DataTable';

type TextRule = { id?: number; analyses_norm_id: number; value_text: string; normalized_code: string; is_allowed?: boolean };

const empty: TextRule = { analyses_norm_id: 0, value_text: '', normalized_code: '', is_allowed: true };

export function TextRulesPage() {
  const [analyses, setAnalyses] = useState<Analyses[]>([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<number>(() => {
    const saved = localStorage.getItem('text_rules_page_analysis_id');
    return saved ? Number(saved) : 0;
  });
  const [norms, setNorms] = useState<any[]>([]);
  const [selectedNormId, setSelectedNormId] = useState<number>(() => {
    const saved = localStorage.getItem('text_rules_page_norm_id');
    return saved ? Number(saved) : 0;
  });
  const [list, setList] = useState<TextRule[]>([]);
  const [form, setForm] = useState<TextRule>({ ...empty });
  const [edit, setEdit] = useState<TextRule | null>(null);

  useEffect(() => {
    Api.listAnalyses().then(a => {
      setAnalyses(a);
      // Восстанавливаем сохраненный анализ или выбираем первый
      const savedAnalysisId = localStorage.getItem('text_rules_page_analysis_id');
      const analysisId = savedAnalysisId ? Number(savedAnalysisId) : (a[0]?.id ?? 0);
      const validAnalysisId = a.find(a => a.id === analysisId) ? analysisId : (a[0]?.id ?? 0);
      if (validAnalysisId > 0) {
        setSelectedAnalysisId(validAnalysisId);
      }
    });
  }, []);

  useEffect(() => {
    async function loadNorms() {
      if (!selectedAnalysisId) return;
      const full = await Api.getAnalysesFullById(selectedAnalysisId);
      const n = full?.norms || [];
      setNorms(n as any[]);
      // Восстанавливаем сохраненную норму или выбираем первую
      const savedNormId = localStorage.getItem('text_rules_page_norm_id');
      const normId = savedNormId ? Number(savedNormId) : (n[0]?.id ?? 0);
      const validNormId = n.find((norm: any) => norm.id === normId) ? normId : (n[0]?.id ?? 0);
      if (validNormId > 0) {
        setSelectedNormId(validNormId);
        setForm(f => ({...f, analyses_norm_id: validNormId}));
        loadRules(validNormId);
      }
    }
    loadNorms();
  }, [selectedAnalysisId]);

  async function loadRules(normId: number) {
    const r = await Api.listTextRulesByNorm(normId);
    console.log('Загружены синонимы для нормы', normId, r);
    const withIds = r.filter((item: any) => item.id);
    const withoutIds = r.filter((item: any) => !item.id);
    if (withoutIds.length > 0) {
      console.warn('Найдены синонимы без ID:', withoutIds);
    }
    setList(r as any);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const created = await Api.createTextRule(form);
    setList([created, ...list]);
    setForm({ ...empty, analyses_norm_id: form.analyses_norm_id });
  }

  async function saveEdit() {
    if (!edit) {
      console.warn('saveEdit: edit is null');
      return;
    }
    if (!edit.id) {
      console.warn('saveEdit: edit.id is missing', edit);
      alert('Не удалось сохранить: у синонима отсутствует ID. Возможно, синоним еще не был сохранен на сервере.');
      return;
    }
    try {
    const updated = await Api.updateTextRule(edit.id, edit);
    setList(list.map(i => (i.id === updated.id ? updated : i)));
    setEdit(null);
    } catch (error) {
      console.error('Ошибка при сохранении синонима:', error);
      alert(`Не удалось сохранить синоним: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  }

  async function deleteRule() {
    if (!edit) {
      console.warn('deleteRule: edit is null');
      return;
    }
    if (!edit.id) {
      console.warn('deleteRule: edit.id is missing', edit);
      alert('Не удалось удалить: у синонима отсутствует ID. Возможно, синоним еще не был сохранен на сервере.');
      return;
    }
    if (confirm('Удалить этот синоним?')) {
      try {
        await Api.deleteTextRule(edit.id);
        setList(list.filter(i => i.id !== edit.id));
        setEdit(null);
      } catch (error) {
        console.error('Ошибка при удалении синонима:', error);
        alert(`Не удалось удалить синоним: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      }
    }
  }

  return (
    <div>
      <div className="toolbar">
        <div className="title"><h2 style={{margin:0}}>Синонимы (текстовые значения)</h2></div>
      </div>
      <div className="row">
        <form className="card" onSubmit={submit} style={{maxWidth:760}}>
          <h3 style={{marginTop:0}}>Новый синоним</h3>
          <label>Анализ</label>
          <SearchSelect
            options={analyses.map(a=>({ value: a.id ?? 0, label: a.analyses_name }))}
            value={selectedAnalysisId}
            onChange={(v)=>{
              const analysisId = Number(v);
              setSelectedAnalysisId(analysisId);
              localStorage.setItem('text_rules_page_analysis_id', String(analysisId));
              localStorage.removeItem('text_rules_page_norm_id');
            }}
          />
          <label>Заключение</label>
          <SearchSelect
            options={norms.map(n=>({ value: n.id, label: `${n.norm_name} (${n.norm_code})` }))}
            value={selectedNormId}
            onChange={(v)=>{
              const id = Number(v);
              setSelectedNormId(id);
              setForm({...form, analyses_norm_id:id});
              loadRules(id);
              localStorage.setItem('text_rules_page_norm_id', String(id));
            }}
          />
          <div className="row">
            <div>
              <label>Ключевые слова нормы</label>
              <input value={form.value_text} onChange={e=>setForm({...form, value_text:e.target.value})} required />
            </div>
            <div>
              <label>Ключевые слова отклонений</label>
              <input value={form.normalized_code} onChange={e=>setForm({...form, normalized_code:e.target.value})} required />
            </div>
            <div>
              <label>Допустимо</label>
              <select value={String(form.is_allowed)} onChange={e=>setForm({...form, is_allowed: e.target.value==='true'})}>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            </div>
          </div>
          <div style={{marginTop:12}}>
            <button>Создать</button>
          </div>
        </form>

        <div style={{flex:2}}>
          <DataTable
            data={list}
            title="Список"
            searchFields={["value_text", "normalized_code"] as any}
            searchPlaceholder="Поиск по тексту/коду..."
            onRowClick={(item)=>setEdit(item)}
            columns={[
              { key: 'value_text', title: 'Ключевые слова нормы' },
              { key: 'normalized_code', title: 'Ключевые слова отклонений' },
              { key: 'is_allowed', title: 'Допустимо', render: (item)=> String(item.is_allowed) }
            ]}
          />
        </div>
      </div>

      {edit && (
        <Modal title={`Синоним #${edit.id}`} onClose={()=>setEdit(null)}>
          <label>Текст</label>
          <input value={edit.value_text} onChange={e=>setEdit({...edit!, value_text:e.target.value})} />
          <label>Норм. код</label>
          <input value={edit.normalized_code} onChange={e=>setEdit({...edit!, normalized_code:e.target.value})} />
          <label>Допустимо</label>
          <select value={String(edit.is_allowed)} onChange={e=>setEdit({...edit!, is_allowed:e.target.value==='true'})}>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
          <div style={{marginTop:12, display:'flex', gap:8, alignItems:'center'}}>
            <div style={{display:'flex', gap:8}}>
              <button onClick={saveEdit} disabled={!edit?.id}>
                Сохранить
              </button>
            <button className="secondary" onClick={()=>setEdit(null)}>Отмена</button>
            </div>
            <button 
              style={{background:'#dc2626', marginLeft:'auto'}} 
              onClick={deleteRule}
              disabled={!edit?.id}
            >
              Удалить
            </button>
          </div>
          {!edit?.id && (
            <p style={{marginTop: 8, color: 'var(--muted)', fontSize: '0.9em', fontStyle: 'italic'}}>
              ⚠️ У этого синонима отсутствует ID. Возможно, он еще не был сохранен на сервере.
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}


